import { router } from "@inertiajs/react"
import { Link2, Mail } from "lucide-react"

import { Button } from "@/components/ui/button"
import { CopyButton } from "@/components/CopyButton"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"


// The coach sends this themselves, so we write the note for them. Kept short
// and in their voice — a long template reads as a form letter, which is the
// whole thing we are avoiding by not sending it ourselves.
function buildEmailTemplate({ memberName, coachName, link }) {
  const first = (memberName || "").split(" ")[0] || "there"
  return `Hi ${first},

I'd love to share your story with the rest of Kingdom Factor — what God's been doing in your business and in our group.

It's not a form. You answer seven questions, one at a time, by typing or just talking. About ten minutes, and you can stop and come back whenever.

${link}

Nothing gets published from it on its own — it comes back to me to edit, and you'll see it before anyone else does.

${coachName}`
}

/**
 * Hands the coach a link to send from their own inbox, the same way the
 * prospect intake invite works. A Member Story is a personal ask; it lands as
 * a note from their coach, not as system mail.
 */
export function MemberStoryInviteDialog({ open, onOpenChange, generated, coachName }) {
  if (!generated) return null

  const emailBody = buildEmailTemplate({
    memberName: generated.name,
    coachName: coachName || "Your coach",
    link: generated.link,
  })

  // The coach revealing they intend to send it. Three signals — copy the link,
  // copy the note, open the mail client — one idempotent stamp server-side.
  //
  // `only` and `preserveState` are load-bearing (spec R2): without `only`, the
  // flash prop refreshes empty, `generated` becomes null, and this dialog
  // unmounts under the coach mid-copy. Without `preserveState`, the parent's
  // open flag resets. Only the roster needs to change.
  const markSent = () =>
    router.post(
      "/app/coach/member_story_invites/mark_sent",
      { account_id: generated.accountId },
      { preserveScroll: true, preserveState: true, only: [ "users" ] }
    )

  const mailtoHref = `mailto:${encodeURIComponent(generated.email)}?subject=${encodeURIComponent(
    "Your Kingdom Factor story"
  )}&body=${encodeURIComponent(emailBody)}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-kf-red" />
            Link ready for {generated.name}
          </DialogTitle>
          <DialogDescription>
            Nothing has been sent yet. Copy the link or the suggested note and
            send it from your own inbox, so it lands as a real message from you
            — copying marks it as sent on your roster.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wide text-zinc-500">
              Their link — signs them in, good for 30 days
            </Label>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row">
              <Input
                value={generated.link}
                readOnly
                onFocus={(event) => event.target.select()}
                className="min-w-0 font-mono text-xs"
              />
              <CopyButton text={generated.link} label="Copy link" onCopied={markSent} />
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Personal to {generated.email} — it signs them in, so send it only to them.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-zinc-500">
              A note you can send
            </Label>
            <pre className="mt-1 max-h-56 overflow-y-auto whitespace-pre-wrap wrap-anywhere rounded-md border border-zinc-200 bg-zinc-50 p-3 font-sans text-sm text-zinc-700">
              {emailBody}
            </pre>
            <div className="mt-2 flex flex-wrap gap-2">
              <CopyButton text={emailBody} label="Copy note" onCopied={markSent} />
              <Button type="button" variant="gradient" size="sm" asChild>
                <a href={mailtoHref} onClick={markSent}>
                  <Link2 className="mr-1 h-3.5 w-3.5" /> Open in email
                </a>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
